import { useEffect, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, FileText, CreditCard, PlugZap, Save, Copy } from "lucide-react";
import { Field, inputCls, selectCls, btnPrimary, btnGhost } from "@/components/erp/ErpUi";

const ENV_OPTS = [["", "Desativado"], ["simulado", "Simulado (sem chave, para testar)"], ["testes", "Ambiente de testes"], ["producao", "Produção"]];

function CopyLine({ label, value, testid }) {
  return (
    <div className="text-xs text-zinc-400 break-all">
      {label}: <span className="text-zinc-200 font-mono">{value}</span>
      <button data-testid={testid} type="button" onClick={() => { navigator.clipboard?.writeText(value); toast.success("Copiado"); }} className="ml-1 text-green-neon align-middle"><Copy className="inline h-3.5 w-3.5" /></button>
    </div>
  );
}

function Block({ kind, title, icon: Icon, cfg, onSaved, children }) {
  const [env, setEnv] = useState(cfg.env || "");
  const [key, setKey] = useState("");
  const [extra, setExtra] = useState({});
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  useEffect(() => { setEnv(cfg.env || ""); setExtra({ company_id: cfg.company_id || "", cfop: cfg.cfop || "", csosn: cfg.csosn || "", service_code: cfg.service_code || "" }); }, [cfg]);
  const save = async () => {
    setBusy(true);
    try {
      const body = { env: env || null, ...(key ? { api_key: key } : {}), ...(kind === "enotas" ? extra : {}) };
      const { data } = await api.patch(`/fiscal/settings/${kind}`, body);
      setKey(""); toast.success("Configuração salva"); onSaved(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const test = async () => {
    setTesting(true);
    try { const { data } = await api.post(`/fiscal/settings/${kind}/test`); toast.success(data.message); }
    catch (e) { toast.error(formatApiError(e)); } finally { setTesting(false); }
  };
  const setX = (k) => (e) => setExtra((p) => ({ ...p, [k]: e.target.value }));
  return (
    <div data-testid={`integration-${kind}`} className="rounded-xl border border-zinc-800 bg-zinc-950/70 p-5 md:p-7 space-y-3">
      <div className="flex items-center gap-2"><Icon className="h-4 w-4 text-amber-brand" /><h2 className="font-display text-lg font-bold uppercase text-zinc-100">{title}</h2>
        <span className={`ml-auto text-[11px] font-bold uppercase ${cfg.configured ? "text-green-neon" : "text-zinc-500"}`}>{cfg.configured ? "Ativo" : "Não configurado"}</span></div>
      {children}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Modo"><select data-testid={`${kind}-env`} value={env} onChange={(e) => setEnv(e.target.value)} className={selectCls}>{ENV_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
        <Field label={`Chave de API ${cfg.api_key_mask ? `(salva ${cfg.api_key_mask})` : ""}`}><Input data-testid={`${kind}-key`} type="password" autoComplete="new-password" value={key} onChange={(e) => setKey(e.target.value)} placeholder={cfg.api_key_mask ? "deixe vazio para manter" : "cole a chave"} className={inputCls} /></Field>
        {kind === "enotas" && <>
          <Field label="ID da empresa no eNotas"><Input data-testid="enotas-company" value={extra.company_id || ""} onChange={setX("company_id")} className={inputCls} /></Field>
          <Field label="Código do serviço na prefeitura (opcional)"><Input data-testid="enotas-service-code" value={extra.service_code || ""} onChange={setX("service_code")} className={inputCls} /></Field>
          <Field label="CFOP das peças"><Input data-testid="enotas-cfop" value={extra.cfop || ""} onChange={setX("cfop")} inputMode="numeric" className={inputCls} /></Field>
          <Field label="CSOSN / CST do ICMS"><Input data-testid="enotas-csosn" value={extra.csosn || ""} onChange={setX("csosn")} inputMode="numeric" className={inputCls} /></Field>
        </>}
      </div>
      <div className="flex gap-2 flex-wrap">
        <Button data-testid={`${kind}-save`} onClick={save} disabled={busy} className={btnPrimary}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-2" /> Salvar</>)}</Button>
        <Button data-testid={`${kind}-test`} variant="outline" onClick={test} disabled={testing || !cfg.configured} className={btnGhost}>{testing ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><PlugZap className="h-4 w-4 mr-2" /> Testar conexão</>)}</Button>
      </div>
    </div>
  );
}

export function IntegrationsCard() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/fiscal/settings").then(({ data }) => setCfg(data)).catch(() => setCfg(null)); }, []);
  if (!cfg) return null;
  return (
    <>
      <Block kind="enotas" title="Nota fiscal (eNotas)" icon={FileText} cfg={cfg.enotas} onSaved={setCfg}>
        <ol className="text-sm text-zinc-400 list-decimal ml-5 space-y-1">
          <li>No painel do eNotas, cadastre sua empresa, os dados fiscais e o certificado digital A1.</li>
          <li>Copie a chave de API e o ID da empresa e cole aqui. Comece em "Ambiente de testes" (homologação).</li>
          <li>Opcional: cadastre o webhook abaixo no eNotas para o status chegar na hora (sem ele, atualizamos a cada 30 min).</li>
        </ol>
        {cfg.enotas.webhook_token && <CopyLine testid="enotas-webhook-copy" label="URL do webhook" value={`${API}/fiscal/webhooks/enotas/${cfg.enotas.webhook_token}`} />}
      </Block>
      <Block kind="asaas" title="Cobrança (Asaas)" icon={CreditCard} cfg={cfg.asaas} onSaved={setCfg}>
        <ol className="text-sm text-zinc-400 list-decimal ml-5 space-y-1">
          <li>No Asaas, vá em Integrações → Chave de API e gere a chave (a de testes fica em sandbox.asaas.com).</li>
          <li>Cole aqui e salve. Depois, em Integrações → Webhooks, crie um webhook com a URL e o token abaixo e os eventos de cobrança.</li>
        </ol>
        {cfg.asaas.webhook_token && <>
          <CopyLine testid="asaas-webhook-url-copy" label="URL do webhook" value={`${API}/fiscal/webhooks/asaas`} />
          <CopyLine testid="asaas-webhook-token-copy" label="Token de autenticação" value={cfg.asaas.webhook_token} />
        </>}
      </Block>
    </>
  );
}
